define('presentation/signup/viewModels/signupSecurityViewModel',[
    'businessServices/signup/signupSessionSingleton',
    'businessServices/signup/signupSessionStore',
    'common/collections/enumerationKeyValueConverter',
    'common/promises/promiseFactory',
    'common/storage/workSessionIdStorage',
    'constants/verificationQuestionConstants',
    'i18next',
    'presentation/signup/common/signupNavigationManager',
    'presentation/signup/facades/signupSecurityFacade',
    'presentation/signup/validators/signupSecurityValidator'
], function() {

    return function() {
        let self = this;

        const PromiseFactoryConstructor = require('common/promises/promiseFactory');
        const _promiseFactory = new PromiseFactoryConstructor();

        const _i18n = require('i18next');
        const _signupSessionState = require('businessServices/signup/signupSessionSingleton');
        const _verificationQuestionsConstants = require('constants/verificationQuestionConstants');
        const _workSessionIdStorage = require('common/storage/workSessionIdStorage');

        const KeyValueConverterConstructor = require('common/collections/enumerationKeyValueConverter');

        const VERIFICATION_QUESTIONS_LIST = new KeyValueConverterConstructor.convertToKeyValues(_verificationQuestionsConstants);
        const DEFAULT_VERIFICATION_QUESTION_ID = VERIFICATION_QUESTIONS_LIST[0].value;

        let _facade = null;
        let _validator = null;
        let _disposables = [];
        let _signupNavigationManager = null;
        let _signupSessionStore = null;

        const _isSignupForCustomer = _workSessionIdStorage.getWorkSessionId() !== null && _workSessionIdStorage.getWorkSessionId() !== undefined;

        let _packageTypeSelection = _signupSessionState.packageTypeSelection;

        const _hasPasswordBeenBlurred = ko.observable(false);
        const _hasConfirmPasswordBeenBlurred = ko.observable(false);

        self.headingText = _i18n.t('signupSecurity:headingText');
        self.subtitleText = _i18n.t('signupSecurity:subtitleText');

        self.password = ko.observable('').extend({ rateLimit: 500 });
        self.confirmPassword = ko.observable('').extend({ rateLimit: 500 });
        self.verificationQuestion = ko.observable([]);
        self.verificationAnswer = ko.observable('').extend({ rateLimit: 500 });
        self.isSignupForCustomer = ko.observable(_isSignupForCustomer);
        self.isCompositionComplete = ko.observable(false);
        self.showValidation = ko.observable(false);
        self.isNavigateToNextPageActive = ko.observable(true);
        self.isScrollActive = ko.observable(false);
        self.isValid = ko.observable(false);
        self.passwordFieldHasFocus = ko.observable(false);
        self.confirmPasswordFieldHasFocus = ko.observable(false);

        self.showPasswordValidation = ko.pureComputed(() => {
            if (self.showValidation()) {
                return true;
            }

            return _hasPasswordBeenBlurred() && self.password().trim().length > 0;
        });

        self.showConfirmPasswordValidation = ko.pureComputed(() => {
            if (self.showValidation()) {
                return true;
            }

            return _hasConfirmPasswordBeenBlurred() && self.confirmPassword().trim().length > 0;
        });

        self.shouldHidePassword = ko.observable(true).toggleable();
        self.shouldHideConfirmPassword = ko.observable(true).toggleable();
        self.verificationQuestions = null;
        self.noResults = "No Results Found";

        self.togglePasswordVisible = () => {
            self.shouldHidePassword.toggle();
            const input = document.getElementById('txt_password');
            input.type = self.shouldHidePassword() ? 'password' : 'text';
        };

        self.toggleConfirmPasswordVisible = () => {
            self.shouldHideConfirmPassword.toggle();
            const input = document.getElementById('txt_confirmPassword');
            input.type = self.shouldHideConfirmPassword() ? 'password' : 'text';
        };

        self.onForwardArrowClicked = () => {
            return _promiseFactory.defer((deferredObject) => {
                _validator.validate()
                    .fail(deferredObject.reject)
                    .done((isValid) => {
                        self.showValidation(true);

                        if (!isValid) {
                            deferredObject.resolve(false);
                        } else {
                            _facade.setSecurityCompleted(true)
                                .fail(deferredObject.reject)
                                .done(() => deferredObject.resolve(true));
                        }
                    });
            });
        };

        self.onBackArrowClicked = () => {
            return _promiseFactory.defer((deferredObject) => {
                if (_packageTypeSelection() !== null) {
                    _updatePackageTypeSelection(null);
                    _signupNavigationManager.previousPage();
                    deferredObject.resolve();
                } else {
                    deferredObject.resolve();
                    _signupNavigationManager.previousPage();
                }
            });
        };

        // Clearing the flag on focus keeps a correction from re-rendering the error on every
        // keystroke; it comes back on the next blur if the field is still invalid.
        const _handlePasswordFocusChanged = (hasFocus) => {
            _hasPasswordBeenBlurred(!hasFocus);
        };

        const _handleConfirmPasswordFocusChanged = (hasFocus) => {
            _hasConfirmPasswordBeenBlurred(!hasFocus);
        };

        const _handlePasswordValidChanged = () => {
            if (self.password()) {
                _facade.savePassword(self.password());
            }
        };

        const _handleVerificationAnswerChanged = () => {
            if (self.verificationAnswer()) {
                _facade.saveVerificationAnswer(self.verificationAnswer());
            }
        };

        const _handleVerificationQuestionChanged = () => {
            const verificationQuestionId = parseInt(self.verificationQuestion()[0]);
            if (verificationQuestionId) {
                _facade.saveVerificationQuestion(verificationQuestionId);
            }
        };

        const _prePopulateInputValues = (signupSecurityPresentationObject) => {
            const { password, verificationQuestionId, verificationAnswer } = signupSecurityPresentationObject;
            if (password) {
                self.password(password);
                self.confirmPassword(password);
            }
            const isValidVerificationQuestionId = VERIFICATION_QUESTIONS_LIST.some(question => question.value === verificationQuestionId);
            if (verificationQuestionId && isValidVerificationQuestionId) {
                self.verificationQuestion([verificationQuestionId]);
            } else {
                self.verificationQuestion([DEFAULT_VERIFICATION_QUESTION_ID]);
                _handleVerificationQuestionChanged();
            }

            if (verificationAnswer) {
                self.verificationAnswer(verificationAnswer);
            }

            if (password && verificationQuestionId && verificationAnswer) {
                self.isValid(true);
            }
        };

        const _populateVerificationQuestionsDropdown = () => {
            self.verificationQuestions = VERIFICATION_QUESTIONS_LIST.map((question) => {
                question.id = question.value;
                question.text = _i18n.t(`signupSecurity:${question.key}`);
                return question;
            });
        };

        const _updatePackageTypeSelection = (type) => {
            return _promiseFactory.defer((promise) => {
                _signupSessionStore.savePackageTypeSelection(type)
                    .done(() => {
                        promise.resolve();
                    });
            });
        };

        self.validatePassword = () => {
            return _promiseFactory.defer((deferredObject) => {
                _validator.validate()
                    .fail(deferredObject.reject)
                    .done((isValid) => {
                        self.showValidation(true);

                        const accountInfo = {
                            userPassword: self.password()
                        };

                        deferredObject.resolve({isValid: isValid, accountInfo: accountInfo});
                    });
            });
        };

        self.detached = () => {
            _disposables.forEach(subscription => subscription.dispose());
            _disposables = [];
        };
        self.compositionComplete = () => {
            self.isCompositionComplete(true);
        };

        self.activate = () => {
            const FacadeConstructor = require('presentation/signup/facades/signupSecurityFacade');
            _facade = new FacadeConstructor();
            _facade.init(_promiseFactory);

            const ValidatorConstructor = require('presentation/signup/validators/signupSecurityValidator');
            _validator = new ValidatorConstructor();

            const SignupNavigationManagerConstructor = require('presentation/signup/common/signupNavigationManager');
            _signupNavigationManager = new SignupNavigationManagerConstructor();
            _signupNavigationManager.init();

            const SignupSessionStoreConstructor = require('businessServices/signup/signupSessionStore');
            _signupSessionStore = new SignupSessionStoreConstructor();
            _signupSessionStore.init();

            return _initialize();
        };

        const _initialize = () => {
            _validator.registerViewModel(self, _facade);

            // Subscribed ahead of the session load so a blur cannot land before we are listening.
            _disposables.push(self.passwordFieldHasFocus.subscribe(_handlePasswordFocusChanged));
            _disposables.push(self.confirmPasswordFieldHasFocus.subscribe(_handleConfirmPasswordFocusChanged));

            _promiseFactory.deferIndefinitely(deferredObject => {
                _populateVerificationQuestionsDropdown();

                _facade.loadSignupSession()
                    .fail(deferredObject.reject)
                    .done((signupProfilePresentationObject) => {
                        _prePopulateInputValues(signupProfilePresentationObject);

                        _disposables.push(self.password.isValid.subscribe(_handlePasswordValidChanged));
                        _disposables.push(self.verificationAnswer.isValid.subscribe(_handleVerificationAnswerChanged));
                        _disposables.push(self.verificationQuestion.subscribe(_handleVerificationQuestionChanged));

                        deferredObject.resolve();
                    });
            });

            return _promiseFactory.wait();
        };
    };
});

